/**
 * Cliente HTTP centralizado.
 * REGRA: nenhum componente ou página deve chamar fetch diretamente.
 *
 * Autenticação: sessão em cookie HTTP-only emitido pelo Laravel Sanctum.
 * NÃO existe token em localStorage/sessionStorage — o navegador envia o cookie
 * automaticamente (credentials: "include") e devolvemos o header X-XSRF-TOKEN
 * lido do cookie XSRF-TOKEN para a proteção CSRF.
 */

const BASE_URL = import.meta.env.VITE_API_URL ?? "/api";

export class HttpError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = "HttpError";
  }
}

/** Erros de validação do Laravel: { campo: [mensagens] } */
export function validationErrors(error: unknown): Record<string, string[]> {
  if (error instanceof HttpError) {
    const body = error.body as { errors?: Record<string, string[]> } | null;
    return body?.errors ?? {};
  }
  return {};
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[2]) : null;
}

let csrfReady = false;

/** Garante o cookie XSRF-TOKEN antes de qualquer requisição que altera estado. */
export async function ensureCsrfCookie(force = false): Promise<void> {
  if (csrfReady && !force && readCookie("XSRF-TOKEN")) return;
  await fetch("/sanctum/csrf-cookie", { credentials: "include" });
  csrfReady = true;
}

type RequestOptions = Omit<RequestInit, "body"> & { body?: unknown; raw?: boolean };

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, method = "GET", raw = false, ...rest } = options;

  if (method !== "GET" && method !== "HEAD") {
    await ensureCsrfCookie();
  }

  const xsrf = readCookie("XSRF-TOKEN");

  const response = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    method,
    credentials: "include",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(xsrf ? { "X-XSRF-TOKEN": xsrf } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const message =
      (payload as { message?: string } | null)?.message ?? `Erro HTTP ${response.status}`;
    throw new HttpError(message, response.status, payload);
  }

  if (raw) return payload as T;

  return (payload as { data?: T })?.data ?? (payload as T);
}

/** Variante que preserva o envelope { data, meta } devolvido pelo Laravel. */
function requestEnvelope<T, M>(path: string): Promise<{ data: T; meta: M }> {
  return request<{ data: T; meta: M }>(path, { method: "GET", raw: true });
}

export const http = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: "GET" }),
  getWithMeta: <T, M>(path: string) => requestEnvelope<T, M>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  delete: <T>(path: string, body?: unknown) => request<T>(path, { method: "DELETE", body }),
};

/** Para serviços cujo contrato inclui o envelope completo (data, meta e outros campos). */
export const httpEnvelope = {
  get: <T>(path: string) => request<T>(path, { method: "GET", raw: true }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body, raw: true }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body, raw: true }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body, raw: true }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE", raw: true }),
};

/** Assinatura de stream SSE — usada para logs e eventos em tempo real. */
export function subscribe(path: string, onMessage: (data: string) => void): () => void {
  const source = new EventSource(`${BASE_URL}${path}`, { withCredentials: true });
  source.onmessage = (event) => onMessage(event.data);
  return () => source.close();
}
